import { Injectable, signal } from '@angular/core';
import { UUID } from '@okcargo-apps/shared-domain';
import { EMessageLevel, IMessageUi, MESSAGE_DEFAULT_EXPIRATION_MS } from '../domain/message-ui.model';

@Injectable({
  providedIn: 'root',
})
export class MessagesUiService {
  private readonly _messages = signal<IMessageUi[]>([]);
  private readonly _timeouts = new Map<UUID, number>();

  constructor() {
    // TODO remove, only for debugging
    // effect(() => {
    //   const currentMessages = this._messages();
    //   const currentTimeouts = Array.from(this._timeouts.entries());
    //   console.log('[MessagesUiService] Estado actualizado:');
    //   console.log('[MessagesUiService] Mensajes:', currentMessages);
    //   console.log('[MessagesUiService] Timeouts:', currentTimeouts);
    // });
  }

  // TODO
  /*
  Hay que modificar lIMessageUi para poder proporcionar en cada mensaje, solo si es necesario,
  una acción que será la que se ejecute si se pulsa un botón. Hay que pasar el identificador de la función
  a llamar en el contenedor (¿o la función misma?) y el label del botón.

  Para eso, el componente también debería emitir valores para que el contenedor detecte qué acción
  se tiene que desencadenar y la ejecute.

  Por ejemplo: se detecta en la aplicación que hay que validar un contacto seleccionado por el usuario.
  Entonces, se añade el mensaje con las instrucciones y el botón.
  El mensaje se almacena en el store.
  En el template del componente padre se muestran los mensajes para esa entidad/página/lo que sea con el
  componente de esta librería.
  El componente de esta librería, si hay acción vinculada al mensaje (¿serviría solo con un nombre o una variable
  o conviene pasarle la referencia a la función a ejecutar?) y label del botón entonces muestra el botón.
  Cuando el usuario pulsa el botón, se emite el identificador (o lo que sea) que detecta el contendor.
  El contenedor ejecuta la acción.

  En función del éxito o no de la acción se elimina el mensaje (todo eso dependerá del contenedor)

   */

  // Implementación en los componentes:
  // funciona con:
  //   public messages = computed(() => this.messagesUiService.getMessagesForObjectId(this.id));
  // por el rastreo de dependencias dinámico: aunque en el computed() no se hace referencia explícita a ningún signal,
  // la referencia al signal sí está en de getMessagesForObjectId() –concretamente, this._messages()–.
  // Esta solución es más eficiente porque el computed, al estar en el componente, se destruye con la destrucción
  // del componente sin necesidad de añadir ningún código adicional, al contrario de lo que sucedería si es el
  // servicio el que crea el computed derivado de un signal.
  public getMessagesForObjectId(objectId: UUID, level?: EMessageLevel): IMessageUi[] {
    return this._messages().filter((msg) =>
      level === undefined ? msg.objectId === objectId : msg.objectId === objectId && msg.level === level,
    );
  }

  public setMessage(message: Partial<IMessageUi>): void {
    if (!message.message || !message.objectId) return;

    const id = message.id ?? crypto.randomUUID();
    const level = message.level ?? EMessageLevel.INFO;
    const durationMs = level === EMessageLevel.ERROR ? 0 : (message.durationMs ?? MESSAGE_DEFAULT_EXPIRATION_MS);

    const fullMessage: IMessageUi = {
      id: id,
      objectId: message.objectId,
      message: message.message,
      level: level,
      durationMs,
      ...(message.type !== undefined && { type: message.type }),
      ...(message.action !== undefined && { action: message.action }),
    };

    this.clearMessageTimeout(id);

    this._messages.update((messages) => {
      const existingIndex = messages.findIndex((msg) => msg.id === id);

      if (existingIndex !== -1) {
        const updated = [...messages];
        updated[existingIndex] = fullMessage;
        return updated;
      }

      return [...messages, fullMessage];
    });

    if (fullMessage.durationMs > 0) {
      const timeoutId = window.setTimeout(() => {
        this.removeMessageById(fullMessage.id);
      }, durationMs);
      this._timeouts.set(fullMessage.id, timeoutId);
    }
  }

  public removeMessageById(id: UUID): void {
    this.clearMessageTimeout(id);
    this._messages.update((messages) => messages.filter((msg) => msg.id !== id));
  }

  public removeMessageByCriteria(criteria: Partial<IMessageUi>): void {
    const messagesToRemove = this._messages().filter((msg) =>
      Object.entries(criteria).every(([key, value]) => msg[key as keyof IMessageUi] === value),
    );

    messagesToRemove.forEach((msg) => this.clearMessageTimeout(msg.id));

    this._messages.update((messages) => messages.filter((msg) => !messagesToRemove.includes(msg)));
  }

  public removeAllMessages(): void {
    this._timeouts.clear();
    this._messages.set([]);
  }

  private clearMessageTimeout(id: UUID): void {
    const timeoutId = this._timeouts.get(id);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      this._timeouts.delete(id);
    }
  }
}
